<div mat-dialog-title class="dialog-title p-3 pr-5">
  <h5>Scheduler</h5>
  <button
    mat-icon-button
    aria-label="Close dialog"
    matTooltip="Close this dialog."
    (click)="onClose()"
    class="dialog-close-button">
    <mat-icon>close</mat-icon>
  </button>
</div>

<hr class="m-0 p-0">

<mat-dialog-content class="px-3 py-4">

  <form #schedulerForm="ngForm">

    <div class="row">

      <div class="col-12 col-xl-6">

        <div class="row mb-3">
          <div class="col-12 col-md-6">
            <mat-slide-toggle [(ngModel)]="form.is_all_day" name="is_all_day" (change)="onAllDayChange()">
              All Day {{ form.is_all_day ? 'Enabled' : 'Disabled' }}
            </mat-slide-toggle>
            <mat-hint class="ml-3">When All Day is enabled, the selected start and end times are ignored and only the selected dates are used.</mat-hint>
          </div>
        </div>

        <div class="row" *ngIf="!form.is_all_day">
          <div class="col-12 col-md-6">
            <mat-form-field class="full-width mb-3" appearance="outline" subscriptSizing="dynamic">
              <mat-label>Duration Unit</mat-label>
              <mat-select
                [(ngModel)]="form.duration_unit"
                name="duration_unit"
                required
                (selectionChange)="onDurationChange()">
                <mat-option *ngFor="let unit of schedulerService.durationUnits" [value]="unit.key">
                  {{ unit.label }}
                </mat-option>
              </mat-select>
              <mat-hint>Select whether the duration is measured in minutes, hours, or days.</mat-hint>
            </mat-form-field>
          </div>
          <div class="col-12 col-md-6">
            <mat-form-field class="full-width mb-3" appearance="outline" subscriptSizing="dynamic">
              <mat-label>Duration</mat-label>
              <input
                matInput
                type="number"
                min="1"
                [(ngModel)]="form.duration_value"
                (blur)="onDurationValueBlur($event)"
                name="duration_value"
                required
                (ngModelChange)="onDurationChange()">
                <span matSuffix class="mr-2">
                  <ng-container [ngSwitch]="form.duration_unit">
                    <span *ngSwitchCase="'minutes'">minute(s)</span>
                    <span *ngSwitchCase="'hours'">hour(s)</span>
                    <span *ngSwitchCase="'days'">day(s)</span>
                  </ng-container>
                </span>
              <mat-hint>Enter how long each scheduled event is expected to last.</mat-hint>
            </mat-form-field>
          </div>
        </div>

        <div class="row">
          <div class="col" >
            <app-datetime-picker
              [selectedDateTime]="scheduleStartDateTime"
              [isRequired]="true"
              [pickerName]="'scheduleStartDateTime'"
              [min]="today"
              [label]="'Schedule Start At'"
              [hint]="form.is_all_day
                  ? 'Select the date when the schedule should start. The selected time will be ignored.'
                  : 'Use the calendar icon to select the schedule start date and time.'"
              [selectTooltip]="'Select the schedule start date and time.'"
              [removeTooltip]="'Clear the schedule start date and time.'"
              [classes]="'full-width mb-3'"
              (pickerDetailsOutput)="getOutputScheduleStartDateTimeDetails($event)"></app-datetime-picker>
          </div>
          <div class="col" *ngIf="scheduleStartDateTime">
            <app-datetime-picker
              [selectedDateTime]="scheduleEndDateTime"
              [pickerName]="'scheduleEndDateTime'"
              [min]="scheduleStartDateTime"
              [label]="'Schedule End At'"
              [hint]="form.is_all_day
                  ? 'Optionally select the final date for this schedule. The selected time will be ignored.'
                  : 'Optionally select the date and time when this schedule should end.'"
              [selectTooltip]="'Select the schedule end date and time.'"
              [removeTooltip]="'Clear the schedule end date and time.'"
              [classes]="'full-width mb-3'"
              (pickerDetailsOutput)="getOutputScheduleEndDateTimeDetails($event)"></app-datetime-picker>
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-6">
            <mat-form-field class="full-width mb-3" appearance="outline" subscriptSizing="dynamic">
              <mat-label>Frequency</mat-label>
              <mat-select
                name="frequency"
                [(ngModel)]="form.frequency"
                (selectionChange)="onFrequencyChange()"
                required>
                <mat-option
                  *ngFor="let frequency of frequencyOptions"
                  [value]="frequency.key">
                  {{ frequency.label }}
                </mat-option>
              </mat-select>
              <mat-hint>How often the schedule should repeat.</mat-hint>
            </mat-form-field>
          </div>
          <div class="col-12 col-md-6">
            <mat-form-field class="full-width mb-3" appearance="outline" subscriptSizing="dynamic">
              <mat-label>Repeat Every</mat-label>
              <input
                matInput
                type="number"
                min="1"
                name="interval"
                [disabled]="form.frequency === 'monthly' && form.monthly_mode === 'multiple_days_months'"
                [(ngModel)]="form.interval"
                (blur)="onIntervalValueBlur($event)"
                (ngModelChange)="onScheduleChange()"
                required>
              <span matSuffix class="mr-2">
                <ng-container [ngSwitch]="form.frequency">
                  <span *ngSwitchCase="'daily'">day(s)</span>
                  <span *ngSwitchCase="'weekly'">week(s)</span>
                  <span *ngSwitchCase="'monthly'">month(s)</span>
                  <span *ngSwitchCase="'yearly'">year(s)</span>
                </ng-container>
              </span>
              <mat-hint>
                {{
                  form.frequency === 'monthly' && form.monthly_mode === 'multiple_days_months'
                    ? 'Repeat Every does not apply when specific months are selected.'
                    : 'Controls how often the schedule repeats.'
                }}
              </mat-hint>
            </mat-form-field>
          </div>
        </div>

        <!-- Weekly -->
        <div class="row" *ngIf="form.frequency === 'weekly'">
          <div class="col-12">
            <label class="d-block mb-2">Repeat On</label>
            <div class="d-flex flex-wrap">
              <button
                *ngFor="let weekday of weekdays"
                mat-stroked-button
                type="button"
                class="mr-2 mb-2"
                [color]="isWeekdaySelected(weekday.key) ? 'primary' : undefined"
                (click)="toggleWeekday(weekday.key)">
                {{ weekday.short_label }}
              </button>
            </div>
            <mat-hint>Select one or more days of the week.</mat-hint>
          </div>
        </div>

        <!-- Monthly -->
        <div *ngIf="form.frequency === 'monthly'" class="mt-0">
          <div class="row">
            <div class="col-12">
              <label class="d-block mb-2">Monthly Pattern</label>
              <mat-radio-group
                [(ngModel)]="form.monthly_mode"
                name="monthly_mode"
                (change)="onMonthlyModeChange()">
                <mat-radio-button value="day_of_month" class="mr-4">
                  Day of Month
                </mat-radio-button>
                <mat-radio-button value="multiple_days" class="mr-4">
                  Multiple Days
                </mat-radio-button>
                <mat-radio-button value="multiple_days_months" class="mr-4">
                  Multiple Days and Months
                </mat-radio-button>
                <mat-radio-button value="weekday" class="mr-4">
                  Weekday
                </mat-radio-button>
                <mat-radio-button value="last_day_of_month">
                  Last Day of Month
                </mat-radio-button>
              </mat-radio-group>
            </div>
          </div>

          <!-- Day of Month -->
          <div class="row mt-3" *ngIf="form.monthly_mode === 'day_of_month'">
            <div class="col-12 col-md-6">
              <mat-form-field class="full-width" appearance="outline" subscriptSizing="dynamic">
                <mat-label>Day of Month</mat-label>
                <input
                  matInput
                  type="number"
                  min="1"
                  max="31"
                  name="monthly_day"
                  [(ngModel)]="form.monthly_day"
                  (ngModelChange)="onScheduleChange()"
                  required>
                <mat-hint>Enter a day between 1 and 31.</mat-hint>
              </mat-form-field>
            </div>
          </div>

          <!-- Multiple Days -->
          <div class="row mt-3" *ngIf="form.monthly_mode === 'multiple_days'">
            <div class="col-12">
              <mat-form-field
                class="full-width"
                appearance="outline"
                subscriptSizing="dynamic">
                <mat-label>Days of Month</mat-label>
                <mat-select
                  [(ngModel)]="form.monthly_days"
                  name="monthly_days"
                  multiple
                  required
                  (selectionChange)="onScheduleChange()">
                  <mat-option
                    *ngFor="let day of monthDays"
                    [value]="day">
                    {{ day }}
                  </mat-option>
                </mat-select>
                <mat-hint>Select one or more days of the month.</mat-hint>
              </mat-form-field>
            </div>
          </div>

          <!-- Multiple Days and Months -->
          <div class="row mt-3" *ngIf="form.monthly_mode === 'multiple_days_months'">
            <div class="col-12 col-md-6">
              <mat-form-field
                class="full-width"
                appearance="outline"
                subscriptSizing="dynamic">
                <mat-label>Days of Month</mat-label>
                <mat-select
                  [(ngModel)]="form.monthly_days"
                  name="monthly_days_months_days"
                  multiple
                  required
                  (selectionChange)="onScheduleChange()">
                  <mat-option
                    *ngFor="let day of monthDays"
                    [value]="day">
                    {{ day }}
                  </mat-option>
                </mat-select>
                <mat-hint>Select one or more days of the month.</mat-hint>
              </mat-form-field>
            </div>
            <div class="col-12 col-md-6">
              <mat-form-field class="full-width" appearance="outline" subscriptSizing="dynamic">
                <mat-label>Months</mat-label>
                <mat-select
                  [(ngModel)]="form.monthly_months"
                  name="monthly_months"
                  multiple
                  required
                  (selectionChange)="onScheduleChange()">
                  <mat-option
                    *ngFor="let month of months"
                    [value]="month.value">
                    {{ month.label }}
                  </mat-option>
                </mat-select>
                <mat-hint>Select the months when the schedule should occur.</mat-hint>
              </mat-form-field>
            </div>
          </div>

          <!-- Weekday -->
          <div class="row mt-3" *ngIf="form.monthly_mode ==='weekday'">
            <div class="col-12 col-md-6">
              <mat-form-field class="full-width" appearance="outline" subscriptSizing="dynamic">
                <mat-label>Position</mat-label>
                <mat-select
                  name="monthly_position"
                  [(ngModel)]="form.monthly_position"
                  (selectionChange)="onScheduleChange()"
                  required>
                  <mat-option
                    *ngFor="let position of monthlyPositions"
                    [value]="position.value">
                    {{ position.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
            <div class="col-12 col-md-6">
              <mat-form-field class="full-width" appearance="outline" subscriptSizing="dynamic">
                <mat-label>Weekday</mat-label>
                <mat-select
                  name="monthly_weekday"
                  [(ngModel)]="form.monthly_weekday"
                  (selectionChange)="onScheduleChange()"
                  required>
                  <mat-option
                    *ngFor="let weekday of weekdays"
                    [value]="weekday.key">
                    {{ weekday.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>

        </div>

        <!-- Summary -->
        <div class="mt-4 mb-3" *ngIf="schedulerSummary">
          <strong>Schedule:</strong>
          {{ schedulerSummary }}
        </div>

        <div class="row m-0 mb-3" *ngIf="hasShortMonthWarning()">
          <div class="col-12 p-0">
            <div class="alert alert-warning" role="alert">
              <mat-icon class="align-middle mr-2">info</mat-icon>
              <small>
                One or more days between the 29th and 31st have been selected. Months that do not contain a selected day will skip that occurrence. To schedule an event on the last available day of every month, select the "Last Day of Month" monthly pattern instead.
              </small>
            </div>
          </div>
        </div>

      </div>

      <div class="col-12 col-xl-6">
        <full-calendar [options]="calendar_options"></full-calendar>
      </div>

    </div>

  </form>

</mat-dialog-content>

<hr class="m-0 p-0">

<mat-dialog-actions class="px-3 py-2">

  <button
    mat-raised-button
    [disabled]="!schedulerForm.valid || !canSave"
    color="primary"
    (click)="onSave()"
    class="my-2 ml-1 mr-2"
    id="save">
    <mat-icon>save</mat-icon>
    Save & Close
  </button>

  <button
    mat-raised-button
    type="button"
    color="secondary"
    class="my-2 ml-1 mr-2"
    (click)="onCancel()">
    <mat-icon>close</mat-icon>
    Cancel
  </button>

</mat-dialog-actions>



